import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, AlertTriangle, RotateCcw } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const email = (params.get('email') || '').toLowerCase().trim();
  const [state, setState] = useState({ status: 'loading', message: '' });
  const [resubLoading, setResubLoading] = useState(false);
  const [resubMsg, setResubMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!email) {
      setState({ status: 'error', message: 'No email address was provided in the link.' });
      return;
    }
    (async () => {
      try {
        const r = await fetch(
          `${BACKEND}/api/nurture/unsubscribe?email=${encodeURIComponent(email)}`,
          { method: 'POST' }
        );
        if (cancelled) return;
        if (r.ok) {
          setState({ status: 'ok', message: '' });
        } else {
          setState({ status: 'error', message: 'Could not process the request. Please try again.' });
        }
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Network error. Please try again later.' });
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const resubscribe = async () => {
    setResubLoading(true);
    try {
      const r = await fetch(
        `${BACKEND}/api/nurture/resubscribe?email=${encodeURIComponent(email)}`,
        { method: 'POST' }
      );
      if (r.ok) {
        setResubMsg("You're back on the list. Welcome back!");
      } else {
        setResubMsg('Could not resubscribe. Please try again later.');
      }
    } catch {
      setResubMsg('Network error.');
    } finally {
      setResubLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F3EE] px-4" data-testid="unsubscribe-page">
      <div className="max-w-[520px] w-full bg-white rounded-xl shadow-sm border border-black/5 p-8 sm:p-10 text-center">
        {state.status === 'loading' && (
          <p className="text-[#3a3a3a]" data-testid="unsubscribe-loading">Processing your request...</p>
        )}
        {state.status === 'ok' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#0B1E3F] flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7 text-[#D4A434]" />
            </div>
            <h1 className="font-heading text-[1.7rem] font-bold text-[#0B1E3F] mb-2" data-testid="unsubscribe-success-heading">
              You&apos;ve been unsubscribed
            </h1>
            <p className="text-[#3a3a3a] mb-6">
              Sorry to see you go. You won&apos;t receive any more emails from this list.
            </p>
            <p className="text-[14px] text-[#666] mb-6">— Coach Lewis</p>
            {!resubMsg && (
              <Button
                variant="outline"
                onClick={resubscribe}
                disabled={resubLoading}
                className="border-[#0B1E3F]/30 text-[#0B1E3F] hover:bg-[#0B1E3F]/5"
                data-testid="unsubscribe-resubscribe-button"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {resubLoading ? 'Resubscribing…' : 'Resubscribe (it was an accident)'}
              </Button>
            )}
            {resubMsg && (
              <p className="text-[#0B1E3F] font-medium" data-testid="unsubscribe-resub-message">{resubMsg}</p>
            )}
            <div className="mt-8 pt-6 border-t border-black/10">
              <Link to="/" className="text-[13px] text-[#0B1E3F] underline" data-testid="unsubscribe-home-link">
                Return to apexathleticmind.com
              </Link>
            </div>
          </>
        )}
        {state.status === 'error' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <AlertTriangle className="h-7 w-7 text-amber-700" />
            </div>
            <h1 className="font-heading text-[1.7rem] font-bold text-[#0B1E3F] mb-2">Something went wrong</h1>
            <p className="text-[#3a3a3a] mb-6" data-testid="unsubscribe-error-message">{state.message}</p>
            <Link to="/" className="text-[14px] text-[#0B1E3F] underline">Return to apexathleticmind.com</Link>
          </>
        )}
      </div>
    </div>
  );
}
